import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { fetchBedrockEstimateStatus, type BedrockEstimateStatus } from "../../api/syncStatus";
import { ErrorState, LoadingState } from "../states";
import { RelativeTimestamp } from "../RelativeTimestamp";
import panel from "./AdminPanel.module.css";
import styles from "./BedrockEstimatePanel.module.css";

/** Renders a count that is unknown until the estimate has read the logs once. */
function useCount(): (value: number | null) => string {
  const { t } = useTranslation();
  return (value) => (value === null ? t("admin.syncStatus.bedrockEstimate.noRead") : value.toLocaleString());
}

/** Renders the marker age as whole minutes, or seconds when under a minute. */
function useAge(): (seconds: number | null) => string {
  const { t } = useTranslation();
  return (seconds) => {
    if (seconds === null) {
      return t("admin.syncStatus.bedrockEstimate.noRead");
    }
    return seconds < 60 ? t("admin.syncStatus.bedrockEstimate.seconds", { count: Math.max(0, Math.round(seconds)) }) : t("admin.syncStatus.bedrockEstimate.minutes", { count: Math.round(seconds / 60) });
  };
}

/**
 * The Bedrock real-time spend estimate panel on Sync status (AIA-109 phase 7): whether the estimate can be trusted right now. Freshness is the
 * estimate job's last successful log read, because a job that stops running raises no alert of its own. State is always stated in words,
 * never by colour alone. It shows counts and model ids only; the API sends no caller ARNs and no failure text.
 *
 * Laid out as a panel: its state as a pill beside the title and a sentence beneath, then the freshness figures and the latest read's
 * counts as tiles (a tile that needs attention says so and is edged in the warning colour), then the CUR-derived prices (AIA-109: the last
 * derivation as tiles, and a table of the rates with their source, Region and date as text), then the three checks (unpriced models,
 * price overrides, rejected invocations), each a line when there is nothing to report and its detail beneath when there is, and the
 * assumptions as small print.
 */
export function BedrockEstimatePanel(): React.JSX.Element | null {
  const { t } = useTranslation();
  const count = useCount();
  const age = useAge();
  const [state, setState] = useState<{ kind: "loading" } | { kind: "loaded"; status: BedrockEstimateStatus } | { kind: "error"; message: string } | { kind: "hidden" }>({ kind: "loading" });

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const result = await fetchBedrockEstimateStatus();
    if (result.kind === "ok") {
      setState({ kind: "loaded", status: result.response });
    } else if (result.kind === "denied") {
      setState({ kind: "hidden" });
    } else {
      setState({ kind: "error", message: result.message });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.kind === "hidden") {
    return null;
  }

  const health = state.kind !== "loaded" ? null : !state.status.enabled ? "off" : state.status.isStale ? "stale" : "fresh";

  return (
    <section className={panel.panel} aria-labelledby="bedrock-estimate-heading">
      <div className={panel.header}>
        <h2 id="bedrock-estimate-heading" className={`panel-title ${panel.title}`}>
          {t("admin.syncStatus.bedrockEstimate.title")}
        </h2>
        {health !== null && (
          <span className={styles.statePill} data-health={health}>
            <span className={styles.stateDot} aria-hidden="true" />
            {t(`admin.syncStatus.bedrockEstimate.health.${health}`)}
          </span>
        )}
      </div>
      <p className={panel.intro}>{t("admin.syncStatus.bedrockEstimate.intro")}</p>
      {state.kind === "loading" && <LoadingState label={t("admin.syncStatus.bedrockEstimate.loading")} />}
      {state.kind === "error" && <ErrorState title={t("admin.syncStatus.bedrockEstimate.errorTitle")} description={state.message} onRetry={() => void load()} />}
      {state.kind === "loaded" && <PanelBody status={state.status} count={count} age={age} />}
    </section>
  );
}

function PanelBody({ status, count, age }: { status: BedrockEstimateStatus; count: (value: number | null) => string; age: (seconds: number | null) => string }): React.JSX.Element {
  const { t } = useTranslation();
  const alertRaised = status.consecutiveMissedRuns >= status.missedRunsBeforeAlert;
  const overLimit = status.enabled && status.isStale;
  const attention = (value: number | null): true | undefined => (value !== null && value > 0) || undefined;

  return (
    <>
      {!status.enabled && (
        <p role="status" className={styles.statusLine} data-health="off">
          {t("admin.syncStatus.bedrockEstimate.off")}
        </p>
      )}
      {status.enabled && (
        <p role="status" className={styles.statusLine} data-health={status.isStale ? "stale" : "fresh"}>
          <strong>{status.isStale ? t("admin.syncStatus.bedrockEstimate.stateStale") : t("admin.syncStatus.bedrockEstimate.stateFresh")}</strong>{" "}
          {status.isStale
            ? t(status.enforceOnEstimate ? "admin.syncStatus.bedrockEstimate.staleEnforcing" : "admin.syncStatus.bedrockEstimate.staleShadow")
            : t("admin.syncStatus.bedrockEstimate.freshExplain")}
        </p>
      )}

      <div>
        <h3 className={panel.subheading}>{t("admin.syncStatus.bedrockEstimate.freshnessHeading")}</h3>
        <dl className={panel.tiles}>
          <div className={panel.tile}>
            <dt>{t("admin.syncStatus.bedrockEstimate.lastRead")}</dt>
            <dd>{status.lastSuccessfulReadAt === null ? t("admin.syncStatus.bedrockEstimate.neverRead") : <RelativeTimestamp isoTimestamp={status.lastSuccessfulReadAt} />}</dd>
          </div>
          <div className={panel.tile} data-attention={overLimit || undefined}>
            <dt>{t("admin.syncStatus.bedrockEstimate.markerAge")}</dt>
            <dd data-empty={status.markerAgeSeconds === null || undefined}>
              {t("admin.syncStatus.bedrockEstimate.ageOfLimit", { age: age(status.markerAgeSeconds), limit: age(status.maxStalenessSeconds) })}
              {overLimit && <span className={panel.tileNote}>{t("admin.syncStatus.bedrockEstimate.overLimit")}</span>}
            </dd>
          </div>
          <div className={panel.tile} data-attention={alertRaised || undefined}>
            <dt>{t("admin.syncStatus.bedrockEstimate.missedRuns")}</dt>
            <dd>
              {t("admin.syncStatus.bedrockEstimate.missedOfAlert", { missed: status.consecutiveMissedRuns, alert: status.missedRunsBeforeAlert })}
              {alertRaised && ` (${t("admin.syncStatus.bedrockEstimate.alertRaised")})`}
            </dd>
          </div>
          <div className={panel.tile} data-attention={status.priceRefresh.hadProblems || undefined}>
            <dt>{t("admin.syncStatus.bedrockEstimate.priceRefresh")}</dt>
            <dd>
              {status.priceRefresh.lastRunStartedAt === null ? (
                t("admin.syncStatus.bedrockEstimate.refreshNever")
              ) : (
                <>
                  <RelativeTimestamp isoTimestamp={status.priceRefresh.lastRunFinishedAt ?? status.priceRefresh.lastRunStartedAt} />
                  <span className={panel.tileNote}>
                    {status.priceRefresh.outcome ?? t("admin.syncStatus.bedrockEstimate.refreshRunning")}
                    {status.priceRefresh.hadProblems && ` (${t("admin.syncStatus.bedrockEstimate.refreshProblems")})`}
                  </span>
                </>
              )}
            </dd>
          </div>
        </dl>
      </div>

      <div>
        <h3 className={panel.subheading}>{t("admin.syncStatus.bedrockEstimate.latestReadHeading")}</h3>
        <dl className={panel.tiles}>
          <div className={panel.tile}>
            <dt>{t("admin.syncStatus.bedrockEstimate.invocationsRead")}</dt>
            <dd data-empty={status.invocationsRead === null || undefined}>{count(status.invocationsRead)}</dd>
          </div>
          <div className={panel.tile} data-attention={attention(status.unattributedInvocations)}>
            <dt>{t("admin.syncStatus.bedrockEstimate.unattributed")}</dt>
            <dd data-empty={status.unattributedInvocations === null || undefined}>{count(status.unattributedInvocations)}</dd>
          </div>
          <div className={panel.tile} data-attention={attention(status.unpricedInvocations)}>
            <dt>{t("admin.syncStatus.bedrockEstimate.unpricedInvocations")}</dt>
            <dd data-empty={status.unpricedInvocations === null || undefined}>{count(status.unpricedInvocations)}</dd>
          </div>
          <div className={panel.tile}>
            <dt>{t("admin.syncStatus.bedrockEstimate.overridePricedInvocations")}</dt>
            <dd data-empty={status.overridePricedInvocations === null || undefined}>{count(status.overridePricedInvocations)}</dd>
          </div>
          <div className={panel.tile} data-attention={attention(status.overrideCacheUncountedInvocations)}>
            <dt>{t("admin.syncStatus.bedrockEstimate.overrideCacheUncounted")}</dt>
            <dd data-empty={status.overrideCacheUncountedInvocations === null || undefined}>{count(status.overrideCacheUncountedInvocations)}</dd>
          </div>
          <div className={panel.tile} data-attention={attention(status.rejectedInvocations)}>
            <dt>{t("admin.syncStatus.bedrockEstimate.rejected")}</dt>
            <dd data-empty={status.rejectedInvocations === null || undefined}>{count(status.rejectedInvocations)}</dd>
          </div>
        </dl>
      </div>

      <div>
        <h3 className={panel.subheading}>{t("admin.syncStatus.bedrockEstimate.curPrices.heading")}</h3>
        <p className={styles.checkNote}>{t("admin.syncStatus.bedrockEstimate.curPrices.intro")}</p>
        <dl className={panel.tiles}>
          <div className={panel.tile}>
            <dt>{t("admin.syncStatus.bedrockEstimate.curPrices.lastRun")}</dt>
            <dd>
              {status.curPriceRefresh.lastRunStartedAt === null ? (
                t("admin.syncStatus.bedrockEstimate.curPrices.neverRun")
              ) : (
                <>
                  <RelativeTimestamp isoTimestamp={status.curPriceRefresh.lastRunFinishedAt ?? status.curPriceRefresh.lastRunStartedAt} />
                  <span className={panel.tileNote}>{status.curPriceRefresh.outcome ?? t("admin.syncStatus.bedrockEstimate.refreshRunning")}</span>
                </>
              )}
            </dd>
          </div>
          <div className={panel.tile} data-attention={status.curPriceRefresh.alertRaised || undefined}>
            <dt>{t("admin.syncStatus.bedrockEstimate.missedRuns")}</dt>
            <dd>
              {t("admin.syncStatus.bedrockEstimate.missedOfAlert", { missed: status.curPriceRefresh.consecutiveMissedRuns, alert: status.curPriceRefresh.missedRunsBeforeAlert })}
              {status.curPriceRefresh.alertRaised && ` (${t("admin.syncStatus.bedrockEstimate.alertRaised")})`}
            </dd>
          </div>
        </dl>
        {status.curPrices.length === 0 ? (
          <p className={styles.checkNote}>{t("admin.syncStatus.bedrockEstimate.curPrices.none")}</p>
        ) : (
          <div className={styles.tableFrame}>
            <table className={styles.table}>
              <caption className="visually-hidden">{t("admin.syncStatus.bedrockEstimate.curPrices.heading")}</caption>
              <thead>
                <tr>
                  <th scope="col">{t("admin.syncStatus.bedrockEstimate.curPrices.model")}</th>
                  <th scope="col">{t("admin.syncStatus.bedrockEstimate.curPrices.region")}</th>
                  <th scope="col">{t("admin.syncStatus.bedrockEstimate.curPrices.source")}</th>
                  <th scope="col">{t("admin.syncStatus.bedrockEstimate.curPrices.input")}</th>
                  <th scope="col">{t("admin.syncStatus.bedrockEstimate.curPrices.output")}</th>
                  <th scope="col">{t("admin.syncStatus.bedrockEstimate.curPrices.derivedOn")}</th>
                </tr>
              </thead>
              <tbody>
                {status.curPrices.map((price) => (
                  <tr key={`${price.modelId}|${price.region}`}>
                    <th scope="row">{price.modelId}</th>
                    <td>{price.region}</td>
                    <td>{price.source === "cur" ? t("admin.syncStatus.bedrockEstimate.curPrices.sourceCur") : price.source}</td>
                    <td>{price.inputPerMillion}</td>
                    <td>{price.outputPerMillion}</td>
                    {/* The API sends an ISO timestamp in UTC, so its first ten characters are the UTC date. */}
                    <td>{price.derivedAt.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <h3 className={panel.subheading}>{t("admin.syncStatus.bedrockEstimate.checksHeading")}</h3>
        <ul className={styles.checks}>
          <Check title={t("admin.syncStatus.bedrockEstimate.unpricedTitle")} clear={status.unpricedModels.length === 0} clearText={t("admin.syncStatus.bedrockEstimate.unpricedNone")}>
            <p className={styles.checkNote}>{t("admin.syncStatus.bedrockEstimate.unpricedNote")}</p>
            <div className={styles.tableFrame}>
              <table className={styles.table}>
                <caption className="visually-hidden">{t("admin.syncStatus.bedrockEstimate.unpricedTitle")}</caption>
                <thead>
                  <tr>
                    <th scope="col">{t("admin.syncStatus.bedrockEstimate.model")}</th>
                    <th scope="col">{t("admin.syncStatus.bedrockEstimate.invocations")}</th>
                    <th scope="col">{t("admin.syncStatus.bedrockEstimate.lastSeen")}</th>
                  </tr>
                </thead>
                <tbody>
                  {status.unpricedModels.map((model) => (
                    <tr key={model.modelId}>
                      <th scope="row">{model.modelId}</th>
                      <td>{model.invocationCount.toLocaleString()}</td>
                      <td>
                        <RelativeTimestamp isoTimestamp={model.lastSeenAt} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Check>

          <Check title={t("admin.syncStatus.bedrockEstimate.overridesTitle")} clear={status.priceOverrides.length === 0} clearText={t("admin.syncStatus.bedrockEstimate.overridesNone")} neutral>
            <p className={styles.checkNote}>{t("admin.syncStatus.bedrockEstimate.overridesNote")}</p>
            <p className={styles.checkNote}>
              {t("admin.syncStatus.bedrockEstimate.overridesCount", { count: status.priceOverrides.length })}{" "}
              <Link to="/admin/providers/bedrock-prices">{t("admin.syncStatus.bedrockEstimate.overridesLink")}</Link>
            </p>
          </Check>

          <Check title={t("admin.syncStatus.bedrockEstimate.rejectedTitle")} clear={status.rejected.length === 0} clearText={t("admin.syncStatus.bedrockEstimate.rejectedNone")}>
            <ul className={styles.rejectedList}>
              {status.rejected.map((entry) => (
                <li key={entry.modelAndOperation}>{t("admin.syncStatus.bedrockEstimate.rejectedItem", { name: entry.modelAndOperation, count: entry.invocations })}</li>
              ))}
            </ul>
          </Check>
        </ul>
      </div>

      <div className={panel.footnotes}>
        <p>{t("admin.syncStatus.bedrockEstimate.cacheAssumption")}</p>
        <p>{t("admin.syncStatus.bedrockEstimate.callersNote")}</p>
      </div>
    </>
  );
}

/**
 * One check: its title and, when there is nothing to report, a line saying so beside a tick; otherwise a mark in the warning colour
 * (or a neutral one, for a list that is information rather than a problem, such as the price overrides) and the detail beneath.
 */
function Check({ title, clear, clearText, neutral = false, children }: { title: string; clear: boolean; clearText: string; neutral?: boolean; children: React.ReactNode }): React.JSX.Element {
  const tone = clear ? "clear" : neutral ? "info" : "attention";
  return (
    <li className={styles.check} data-tone={tone}>
      <div className={styles.checkHead}>
        <span className={styles.checkIcon} aria-hidden="true">
          <svg viewBox="0 0 16 16" focusable="false">
            {tone === "clear" ? (
              <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
            ) : tone === "info" ? (
              <path d="M8 7v4.5M8 4.5v.01" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
            ) : (
              <path d="M8 4v5M8 11.5v.01" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
            )}
          </svg>
        </span>
        <h4 className={styles.checkTitle}>{title}</h4>
        {clear && <p className={styles.checkClear}>{clearText}</p>}
      </div>
      {!clear && <div className={styles.checkBody}>{children}</div>}
    </li>
  );
}
